/**
 * Когда выдан токен планшета — без раскрытия самого токена.
 *
 * Бекенд подписывает токены itsdangerous (`TimestampSigner`): формат
 * `payload.timestamp.signature`, у сжатого payload в начале точка. Метка
 * времени — секунды эпохи, big-endian, base64url без `=`. Срок жизни
 * в токене не записан — его знает только сервер (сейчас 1 ч / 24 ч,
 * на стенде .70 — 2 / 4 мин).
 */

/** Метка времени itsdangerous не длиннее 8 байт. */
const MAX_STAMP_BYTES = 8;

/** Разбор big-endian: каждый следующий байт — младший разряд. */
const BYTE_RADIX = 256;

const MILLISECONDS_IN_SECOND = 1000;

/** Границы правдоподобной даты выдачи. */
const MIN_YEAR = 2020;
const MAX_YEAR = 2100;

/** Минимальное число частей токена: `payload.timestamp.signature`. */
const MIN_TOKEN_PARTS = 3;

export function tokenIssuedAt(token: string | null | undefined): Date | null {
  if (token === null || token === undefined || token.length === 0) return null;
  const parts = token.split('.');
  if (parts.length < MIN_TOKEN_PARTS) return null;
  const stamp = parts[parts.length - 2];
  if (stamp.length === 0) return null;

  const bytes = decodeBase64Url(stamp);
  if (bytes === null) return null;
  if (bytes.length === 0 || bytes.length > MAX_STAMP_BYTES) return null;

  let seconds = 0;
  for (const b of bytes) {
    seconds = seconds * BYTE_RADIX + b;
  }

  const time = new Date(seconds * MILLISECONDS_IN_SECOND);
  const year = time.getFullYear();
  // Всё, что не похоже на дату выдачи, — не наш формат. NaN здесь значит то же,
  // что исключение `DateTime.fromMillisecondsSinceEpoch` в Dart: метка вышла за
  // пределы, которые умеет представлять дата.
  if (Number.isNaN(year) || year < MIN_YEAR || year > MAX_YEAR) return null;
  return time;
}

/**
 * base64url → байты. В Dart это `base64Url.decode(base64Url.normalize(stamp))`:
 * дополняет `=` и принимает оба алфавита. `atob` понимает только `+/`, поэтому
 * символы заменяются вручную, а padding дописывается сам.
 *
 * Текст здесь не декодируется (ни `TextDecoder`, ни `decodeURIComponent`):
 * в метке лежит целое число big-endian, так что коды символов, которые вернул
 * `atob`, и есть искомые байты — каждый строго в диапазоне 0..255.
 *
 * Битый токен не роняет вызов: как и Dart-версия, возвращается `null`.
 */
function decodeBase64Url(input: string): Uint8Array | null {
  const base64 = input.replaceAll('-', '+').replaceAll('_', '/');
  const remainder = base64.length % 4;
  // Длина 4n+1 в base64 невозможна — Dart на такой строке бросал FormatException.
  if (remainder === 1) return null;
  const padded =
    remainder === 0 ? base64 : base64 + '='.repeat(4 - remainder);
  try {
    const binary = atob(padded);
    return Uint8Array.from(binary, (char) => char.charCodeAt(0));
  } catch {
    // Недопустимые символы в метке — не наш формат.
    return null;
  }
}
